/**
 * Utilities for handling HTTP requests and error formatting
 */

export interface FormattedError {
  /** Mensaje de error legible para el usuario */
  message: string;
  /** Código de estado HTTP */
  statusCode?: number;
  /** Si debería hacer retry */
  shouldRetry: boolean;
  /** Respuesta original de la API para acceso a datos adicionales */
  response?: any;
}

/**
 * Formats error messages from API responses
 * @param error - The error object from axios or other HTTP client
 * @returns Formatted error object with essential information
 */
export const formatError = (error: any): FormattedError => {
  const statusCode = error.response?.status;
  const responseBody = error.response?.data;

  // Determinar si debería hacer retry
  const isNetworkError =
    !error.response &&
    (error.code === 'NETWORK_ERROR' || error.message?.includes('Network'));
  const isClientError = statusCode >= 400 && statusCode < 500;
  const isServerError = statusCode >= 500 && statusCode < 600;

  // Solo retry en errores de servidor o red, no en errores de cliente
  const shouldRetry = isServerError || isNetworkError;

  // Extraer mensaje de error
  let message = 'Error inesperado';

  if (
    responseBody?.error?.message &&
    typeof responseBody.error.message === 'string'
  ) {
    message = responseBody.error.message;
  } else if (responseBody?.error && typeof responseBody.error === 'string') {
    message = responseBody.error;
  } else if (
    responseBody?.message &&
    typeof responseBody.message === 'string'
  ) {
    message = responseBody.message;
  } else if (responseBody?.data && typeof responseBody.data === 'string') {
    message = responseBody.data;
  } else if (responseBody?.errors && typeof responseBody.errors === 'string') {
    message = responseBody.errors;
  } else if (error.message) {
    message = error.message;
  }

  return {
    message,
    statusCode,
    shouldRetry,
    response: error.response,
  };
};

/**
 * Legacy function for backward compatibility
 * @deprecated Use formatError instead for better error handling
 */
export const formatErrorLegacy = (error: any): string => {
  return formatError(error).message;
};

/**
 * Extracts page parameter from URL
 * @param urlP - The URL string to parse
 * @returns The page parameter value or null
 */
export const getPageParam = (urlP: string): string | null => {
  if (!urlP) return null;
  const url = new URL(urlP);
  const params = new URLSearchParams(url.search);
  const page = params.get('page');
  return page;
};
